import { Injectable } from '@angular/core';
import { AudioTrack } from 'shared/src/components/audio-track/audio-track.component';
import { UserReview } from 'shared/src/components/user-review/user-review.component';
import { buildDynamicRows, COMPETITOR_KLASS, CompetitorFeatureRow } from '../data/competitors';
import { GoalId, highlightText, MotivationId } from '../data/quiz';
import { goalsReviewsMap } from '../data/reviews';
import { FunnelFlowService } from './funnel-flow.service';

declare const $localize: any;

export interface PaywallFeature {
  emoji: string;
  title: string;
  description: string;
}

export interface PaywallFaq {
  question: string;
  answer: string;
}

export interface PaywallAudioTrackSection {
  title: string;
  tracks: AudioTrack[];
}

export interface PaywallComparison {
  opponent: string;
  rows: CompetitorFeatureRow[];
  footnote?: string;
}

export interface PaywallAnimationFiles {
  beforeFileName: string;
  afterFileName: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaywallService {
  constructor(protected flowService: FunnelFlowService) {}

  getTitle(): string {
    return (
      $localize`:@@paywallTitlePrefix:Your ` +
      highlightText($localize`:@@paywallTitleHighlight:personalized 3-month plan`) +
      $localize`:@@paywallTitleSuffix: is ready`
    );
  }

  getPlanCta(): string {
    return $localize`:@@grabPlanCta:Grab your personal plan before it's gone!`;
  }

  getHighlights(): string[] {
    return [
      $localize`:@@highlight1:Content reading tailored to your lifestyle`,
      $localize`:@@highlight2:Speed control to match your pace`,
      $localize`:@@highlight3:AI picks the right voice for each text based on your profile & content`,
      $localize`:@@highlight4:Insights to stay on track`,
      $localize`:@@highlight5:Life-enhancing habits that last`,
    ];
  }

  getFeatures(): PaywallFeature[] {
    return [
      {
        emoji: '📚',
        title: $localize`:@@feature1Title:Automatically turn all your reading into audio`,
        description: $localize`:@@feature1Desc:Listen to web pages, books, and documents instead of reading them.`,
      },
      {
        emoji: '🔊',
        title: $localize`:@@feature2Title:Right voice for each text`,
        description: $localize`:@@feature2Desc:No robotic voices - just clear, natural speech that fits the content.`,
      },
      {
        emoji: '⏩',
        title: $localize`:@@feature3Title:Speed up playback up to 3x`,
        description: $localize`:@@feature3Desc:Go through content faster without missing important details.`,
      },
      {
        emoji: '📷',
        title: $localize`:@@feature4Title:Scan paper text and listen`,
        description: $localize`:@@feature4Desc:Take a photo of a page and Peech will read it aloud.`,
      },
      {
        emoji: '🧠',
        title: $localize`:@@feature5Title:Perfect for ADHD and focus challenges`,
        description: $localize`:@@feature5Desc:Convert distracting reading into focused listening that helps maintain attention.`,
      },
      {
        emoji: '🌍',
        title: $localize`:@@feature6Title:Used by over 10,000,000 people`,
        description: $localize`:@@feature6Desc:Join millions who use Peech to listen, learn, and save time.`,
      },
    ];
  }

  getAudioTrackSections(): PaywallAudioTrackSection[] {
    const { job, areas } = this.flowService.getUserAudioTracks();

    return [
      {
        title: $localize`:@@forProfessionalGrowth:🎓 For Professional Growth`,
        tracks: job,
      },
      {
        title: $localize`:@@forSelfDevelopment:🎧 For Self-Development`,
        tracks: areas,
      },
    ];
  }

  getFaqs(): PaywallFaq[] {
    return [
      {
        question: $localize`:@@faqGetAccess:How do I get access to the app?`,
        answer: $localize`:@@faqGetAccessAnswer:After you fill in your email address, a mail with app download link will be in your inbox. Just install the app, Sign In with your email.`,
      },
      {
        question: $localize`:@@faqWhyNeedApp:Why do I need the Peech app?`,
        answer: $localize`:@@faqWhyNeedAppAnswer:Peech turns your books, articles, and notes into natural-sounding audio, so you can consume the content anytime, anywhere without being glued to a screen. Whether you're commuting, exercising, or relaxing, Peech helps you reduce eye strain, save time, and stay productive—no matter how busy your day gets.`,
      },
      {
        question: $localize`:@@faqCancelAccount:How do I cancel my account?`,
        answer: $localize`:@@faqCancelAccountAnswer:It's easy! Just log into your account here, and click the Cancel button.`,
      },
      {
        question: $localize`:@@faqConvertReading:How easy is it to convert my reading materials into audio?`,
        answer: $localize`:@@faqConvertReadingAnswer:Extremely easy! Simply upload your documents or links, and Peech will handle the rest.`,
      },
      {
        question: $localize`:@@faqUsefulContent:I only want to listen to useful content of the web article. How do I do that?`,
        answer: $localize`:@@faqUsefulContentAnswer:We pull only useful text from any web page or document!`,
      },
      {
        question: $localize`:@@faqAdhdHelp:How does Peech help with ADHD?`,
        answer: $localize`:@@faqAdhdHelpAnswer:Peech is ideal for those with ADHD or focus difficulties. Reading can be overwhelming and hard to concentrate on, but listening engages different neural pathways. Our users with ADHD report greater retention, reduced mental fatigue, and increased productivity when consuming content through audio rather than reading. The ability to control playback speed and listen while moving or doing other activities makes it especially effective for diverse attention styles.`,
      },
    ];
  }

  getSocialProofText(totalUsersThisHour: number): string {
    return $localize`:@@peopleJoinedLastHour:${totalUsersThisHour}:INTERPOLATION: people joined Peech community in the last hour`;
  }

  getCommunityTitle(): string {
    return (
      $localize`:@@joinMillionsSuccessfulPrefix:Join Millions of` +
      highlightText($localize`:@@successfulUsers: Successful Users `) +
      $localize`:@@joinMillionsSuccessfulSuffix:with Peech`
    );
  }

  getPeechMembersPercentStat(): string {
    // Turkish reads "X'in %Y'si" (noun before percent), unlike the English "Y% of X" order,
    // so the percent has to stay a movable, independently-translated segment.
    return (
      $localize`:@@peechMembersPercentPrefix:` +
      `<span class="highlight">${$localize`:@@peechMembersPercentValue:91%`}</span>` +
      $localize`:@@peechMembersPercentSuffix: of Peech members`
    );
  }

  getComparison(): PaywallComparison {
    const goalId = this.flowService.getGoalId() ?? 'beMoreProductive';
    const competitor = this.flowService.getCompetitor();
    const opponentName = competitor ?? $localize`:@@competitorOtherTtsApp:Other TTS app`;
    const rows = buildDynamicRows(goalId, competitor, 6);
    const klass = COMPETITOR_KLASS[competitor ?? ''] ?? 'reader';
    const footnote =
      klass === 'reader'
        ? $localize`:@@footnoteCompetitorsLimited:Note: Some competitors offer limited versions of these features.`
        : $localize`:@@footnoteStudioTools:Note: Studio voice tools focus on content creation, not long-form personal reading.`;

    return { opponent: opponentName, rows, footnote };
  }

  getAnimationFiles(): PaywallAnimationFiles {
    const gender = this.flowService.getGender() || 'female';

    return {
      beforeFileName: gender === 'male' ? 'man_sad_before' : 'woman_sad_before',
      afterFileName: gender === 'male' ? 'man_happy_after' : 'woman_happy_after',
    };
  }

  getContextualTextBundle(): { title: string; beforeText: string; afterText: string } {
    let title = $localize`:@@contextGrowth:Growth`;
    let beforeText = $localize`:@@beforeLostBackground:Lost in the background noise`;
    let afterText = $localize`:@@afterStandingOut:Standing out and getting noticed`;
    const goalId = this.flowService.getGoalId();

    if (!goalId) {
      return { title, beforeText, afterText };
    }

    const goalTextsById: Record<GoalId, { title: string; beforeText: string; afterText: string }> =
      {
        beMoreProductive: {
          title: $localize`:@@contextProductivity:Productivity`,
          beforeText: $localize`:@@beforeStrugglingProd:Struggling with productivity`,
          afterText: $localize`:@@afterMaximizingProd:Maximizing your productivity`,
        },
        everythingIsPerfect: {
          title: $localize`:@@contextProductivity:Productivity`,
          beforeText: $localize`:@@beforeStrugglingProd:Struggling with productivity`,
          afterText: $localize`:@@afterMaximizingProd:Maximizing your productivity`,
        },
        learnSomethingNew: {
          title: $localize`:@@contextLearning:Learning`,
          beforeText: $localize`:@@beforeUnableAbsorb:Unable to absorb new information`,
          afterText: $localize`:@@afterEffortlesslyAbsorb:Effortlessly absorbing new knowledge`,
        },
        listenOnTheGo: {
          title: $localize`:@@contextReading:Reading`,
          beforeText: $localize`:@@beforeWastingTime:Wasting valuable time`,
          afterText: $localize`:@@afterEveryMinute:Making every minute count`,
        },
        makeReadingEasier: {
          title: $localize`:@@contextReading:Reading`,
          beforeText: $localize`:@@beforeOverwhelmed:Overwhelmed by reading material`,
          afterText: $localize`:@@afterConsumingEase:Consuming content with ease`,
        },
        adhdEyeStrainDyslexia: {
          title: $localize`:@@contextWellBeing:Well-being`,
          beforeText: $localize`:@@beforeStraining:Straining your eyes and mind`,
          afterText: $localize`:@@afterConsumingEase:Consuming content with ease`,
        },
      };

    title = goalTextsById[goalId].title;
    beforeText = goalTextsById[goalId].beforeText;
    afterText = goalTextsById[goalId].afterText;

    const motivationTextsById: Record<
      MotivationId,
      { title: string; beforeText: string; afterText: string }
    > = {
      advanceCareer: {
        title: $localize`:@@contextCareerGrowth:Career Growth`,
        beforeText: $localize`:@@beforeStalledProfessionally:Feeling stalled professionally`,
        afterText: $localize`:@@afterAcceleratingCareer:Accelerating your career trajectory`,
      },
      excelEducation: {
        title: $localize`:@@contextAcademicSuccess:Academic Success`,
        beforeText: $localize`:@@beforeStrugglingStudies:Struggling to keep up with studies`,
        afterText: $localize`:@@afterTopMarks:Achieving top marks with ease`,
      },
      supportMentalHealth: {
        title: $localize`:@@contextMentalWellBeing:Mental Well-being`,
        beforeText: $localize`:@@beforeMentallyDrained:Mentally drained and overwhelmed`,
        afterText: $localize`:@@afterCalmBalanced:Calm, balanced learning sessions`,
      },
      manageAdhd: {
        title: $localize`:@@contextAdhdSupport:ADHD Support`,
        beforeText: $localize`:@@beforeStrugglingTask:Struggling to stay on task`,
        afterText: $localize`:@@afterChannelingEnergy:Channeling energy into focused growth`,
      },
      improveFocus: {
        title: $localize`:@@contextFocusAttention:Focus & Attention`,
        beforeText: $localize`:@@beforeEasilyDistracted:Easily distracted`,
        afterText: $localize`:@@afterDeeplyFocused:Deeply focused and engaged`,
      },
      overcomeDyslexia: {
        title: $localize`:@@contextAccessibleReading:Accessible Reading`,
        beforeText: $localize`:@@beforeTextIntimidating:Text feels intimidating`,
        afterText: $localize`:@@afterContentEffortless:Content becomes effortless to absorb`,
      },
      reduceEyeStrain: {
        title: $localize`:@@contextEyeHealth:Eye Health`,
        beforeText: $localize`:@@beforeEyesTired:Eyes tired from screens`,
        afterText: $localize`:@@afterEyesBreak:Giving your eyes a well-deserved break`,
      },
      saveTime: {
        title: $localize`:@@contextTimeEfficiency:Time Efficiency`,
        beforeText: $localize`:@@beforeRunningOutHours:Running out of hours`,
        afterText: $localize`:@@afterReclaimingTime:Reclaiming precious time`,
      },
      enjoyEntertainment: {
        title: $localize`:@@contextReadingFun:Reading for Fun`,
        beforeText: $localize`:@@beforeMomentsBoredom:Long moments of boredom`,
        afterText: $localize`:@@afterEntertainmentGrowth:Entertainment that fuels growth`,
      },
      personalGrowth: {
        title: $localize`:@@contextSelfImprovement:Self-Improvement`,
        beforeText: $localize`:@@beforeUnsureLevelUp:Unsure how to level up`,
        afterText: $localize`:@@afterGrowingDaily:Growing a bit every single day`,
      },
    };

    if (goalId === 'listenOnTheGo') {
      for (const motivationId of this.flowService.getMotivationIds()) {
        if (motivationTextsById[motivationId]) {
          title = motivationTextsById[motivationId].title;
          break;
        }
      }
    }

    for (const motivationId of this.flowService.getMotivationIds()) {
      if (motivationTextsById[motivationId]) {
        beforeText = motivationTextsById[motivationId].beforeText;
        afterText = motivationTextsById[motivationId].afterText;
        break;
      }
    }

    return { title, beforeText, afterText };
  }

  getReviews(): UserReview[] {
    const goalId = this.flowService.getGoalId();

    if (goalId && goalsReviewsMap[goalId]) {
      return goalsReviewsMap[goalId];
    }

    return goalsReviewsMap.beMoreProductive;
  }
}
